import { useEffect, useState } from "react";
import FormPrint from "../../Survey/PrintResponse/FormPrint";
import { Button, CircularProgress } from "@mui/material";
import { useQuery } from "../../../utils/hooks";
import { errorToastMessage } from "../../../utils/toast";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getFormData } from "../../../Redux/actions/responseAction";
import {
  setRepeatedAttemptId,
  setScreeningAttemptId,
  setIsRegistryParticipant,
  setParticipantId,
} from "../../../Redux/reducers/responseSlice";

const FormResponsePrint = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const questionList = useAppSelector((state) => state.response.questionList);
  const isLoading = useAppSelector((state) => state.response.isLoading);

  const studyId = useQuery().get("studyId") || undefined;
  const formId = useQuery().get("formId") || undefined;
  const formName = useQuery().get("formName") || "";
  const participantId = useQuery().get("participantId") || undefined;
  const category = useQuery().get("category") || undefined;
  const repeatedAttemptId = useQuery().get("repeatedAttemptId") || undefined;
  const screeningAttemptId = useQuery().get("screeningAttemptId") || undefined;
  const isRegistryParticipant =
    useQuery().get("isRegistryParticipant") === "true";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!formId || !participantId) {
          errorToastMessage(new Error("Missing required parameters"));
          setLoading(false);
          return;
        }

        // For non-registry participants, studyId is required
        if (!isRegistryParticipant && !studyId) {
          errorToastMessage(new Error("Missing required parameters"));
          setLoading(false);
          return;
        }

        // Set Redux state first (getFormData reads from Redux state)
        if (participantId) {
          dispatch(setParticipantId(participantId));
        }
        if (repeatedAttemptId) {
          dispatch(setRepeatedAttemptId({ repeatedAttemptId }));
        }
        if (screeningAttemptId) {
          dispatch(setScreeningAttemptId({ screeningAttemptId }));
        }
        if (isRegistryParticipant) {
          dispatch(setIsRegistryParticipant(isRegistryParticipant));
        }

        // Create selectedForm object with name from query params
        const selectedFormObj = {
          id: formId,
          name: formName,
        };

        // Dispatch getFormData - it will fetch and set questions in Redux
        // For registry participants, studyId is not passed
        dispatch(
          getFormData({
            selectedForm: selectedFormObj as any,
            ...(studyId && { studyId }),
            participantId,
          })
        );
      } catch (err) {
        errorToastMessage(err as Error);
        setLoading(false);
      }
    };
    fetchData();
  }, [
    dispatch,
    formId,
    formName,
    studyId,
    participantId,
    category,
    repeatedAttemptId,
    screeningAttemptId,
    isRegistryParticipant,
  ]);

  // Watch for questions in Redux state after getFormData completes
  useEffect(() => {
    // getFormData sets isLoading to false when done
    if (!isLoading && questionList && questionList.length > 0) {
      // Sort Choices - create a copy to avoid mutating read-only Redux state
      const sortedQuestions = questionList.map((q) => {
        const questionCopy = { ...q };
        if (questionCopy.choices) {
          questionCopy.choices = [...questionCopy.choices].sort(
            (a, b) => a.position - b.position
          );
        }
        return questionCopy;
      });

      const formData = {
        id: formId,
        name: formName,
        questions: sortedQuestions,
      };

      setForms([formData]);
      setLoading(false);
    }
  }, [questionList, isLoading, formName, formId]);

  const startPrint = () => {
    window.print();
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        backgroundColor: "white",
      }}
    >
      <CircularProgress size={50} />
      <div style={{ marginTop: "10px" }}>
        Please wait while we generate the forms...
      </div>
    </div>
  ) : (
    <div className="study-print-container" style={{ backgroundColor: "white" }}>
      <div className="print-button">
        <Button variant="contained" onClick={startPrint}>
          Print
        </Button>
      </div>
      {forms?.map((form, i) => (
        <FormPrint
          key={form.id}
          form={form}
          breakPage={i === 0 ? false : true}
        />
      ))}
    </div>
  );
};

export default FormResponsePrint;
